<script setup lang="ts">
import { computed } from 'vue'
import CoreIcon from '@core/core-icon.vue';
import {hapticFeedback, openLink, openTelegramLink} from "@tma.js/sdk-vue";
import isInTelegram from "@/utils/isInTelegram";

const props = defineProps({
	to: {
		type: [String, Object],
		default: ''
	},
	url: {
		type: String,
		default: null
	},
	icon: {
		type: Object,
		default: null
	},
	horizontalButton: {
		type: Boolean,
		default: false
	},
	// Иконка слева, текст справа, в одну строку — третий вариант раскладки
	// рядом с вертикальной (по умолчанию) и горизонтальной (иконка справа)
	iconLeft: {
		type: Boolean,
		default: false
	},
	bordered: {
		type: Boolean,
		default: false
	},
	gradient: {
		type: Boolean,
		default: false
	},
	loading: {
		type: Boolean,
		default: false
	},
	font: {
		type: String,
		default: ""
	}
});

const modes = computed(() => ({
	'button-blurred_horizontal': props.horizontalButton,
	'button-blurred_icon-left': props.iconLeft,
	'button-blurred_bordered': props.bordered,
	'button-blurred_gradient': props.gradient,
	'button-blurred_loading': props.loading,
}));

const linkAttributes = computed(() => {
	const attrs = {};
	if (props?.to) {
		attrs.to = props.to;
	}
	if (props?.url) {
		attrs.href = props.url;
		attrs.target = '_blank';
	}
	return attrs;
});

const isTelegramLink = (url: string) => /^(https?:\/\/(t|telegram)\.me\/|tg:\/\/)/i.test(url);
const isWebLink = (url: string) => /^https?:\/\//i.test(url);

// hrefs use telegram.me because t.me is DNS-blocked in some regions, but
// openTelegramLink() accepts only https://t.me/… — inside Telegram the link is
// passed to the client via postEvent, so DNS availability doesn't matter there.
const toTgMeUrl = (url: string) => url.replace(/^https?:\/\/(t|telegram)\.me\//i, 'https://t.me/');

const clickHandler = async (event: MouseEvent) => {
  if (isInTelegram() && hapticFeedback.impactOccurred.isAvailable()) {
    hapticFeedback.impactOccurred('medium');
  }
  if (!props.url || !isInTelegram()) {
    return;
  }
  // Inside the Mini App a plain target=_blank can close it or hand the link to
  // the system browser; the SDK methods open the link and keep the Mini App alive.
  if (isTelegramLink(props.url)) {
    if (openTelegramLink.isAvailable()) {
      event.preventDefault();
      openTelegramLink(toTgMeUrl(props.url));
    }
    return;
  }
  if (isWebLink(props.url) && openLink.isAvailable()) {
    event.preventDefault();
    openLink(props.url);
  }
}
</script>

<template>
	<component
		:is="to ? 'router-link' : (url || loading) ? 'a' : 'button'"
		v-bind="linkAttributes"
		:class="['button-blurred', modes]"
    @click.native="clickHandler"
	>
		<span
			v-if="icon && icon?.name"
			class="button-blurred__icon"
		>
			<core-icon
				:name="icon.name"
				:viewBox="icon?.viewBox || '0 0 16 16'"
			>
			</core-icon>
		</span>
		<span :class="['button-blurred__text', font]">
			<slot />
		</span>
	</component>
</template>
<style lang="scss">
.button-blurred {
	background: var(--color-panel);
	border: 1px solid var(--color-panel);
	fill: var(--color-active);

	padding: 1.2rem;
	border-radius: 1.6rem;
	display: flex;
	flex-direction: column;
	gap: 1rem;
	cursor: pointer;
	position: relative;
	overflow: hidden;

	text-align: left;

	transition: background-color ease 0.2s;

	@include min-desktop {
		border-radius: 2.4rem;
		gap: 1.6rem;
	}

	// Подсветка там, где есть курсор, а не там, где широкий экран: в узком
	// окне на ноутбуке hover тоже нужен, а на тач-экране он только залипает
	@media (hover: hover) and (pointer: fine) {
		&:not(.button-blurred_gradient):hover {
			background: rgba(var(--color-active-rgb), 0.1);
		}
	}

	&_bordered {
		border: 1px solid rgba(255, 255, 255, 0.06);
	}

	&_horizontal {
		width: 100%;
		min-height: 6.2rem;
		padding: 0.7rem 0.7rem 0.7rem 1.7rem;
		flex-direction: row-reverse;
		align-items: center;
		justify-content: space-between;

		@include min-desktop {
			padding-left: 2.4rem;
			padding-right: 2.4rem;
		}

		.button-blurred {
			&__text {
				margin-top: 0;
			}
		}
	}

	// Для узких кнопок в два столбца: вертикальная раскладка оставляла бы
	// пустоту под иконкой, а горизонтальная прячет иконку в правый край.
	&_icon-left {
		flex-direction: row;
		align-items: center;
		gap: 1rem;
		min-height: 5.2rem;
		padding: 1rem 1.2rem;

		@include min-desktop {
			min-height: 6.2rem;
			gap: 1.2rem;
			padding-left: 1.6rem;
			padding-right: 1.6rem;
		}

		.button-blurred {
			&__icon {
				flex: 0 0 auto;
			}

			&__text {
				margin-top: 0;
				min-width: 0;
				line-height: 1.2;
			}
		}
	}

	&_gradient {
		border: none;
		background: linear-gradient(to bottom, #F7F5FC, #F4E6F6);
		&::before {
			content: '';
			position: absolute;
			left: 0;
			top: 0;
			width: 100%;
			height: 100%;
			background: #F4E6F6;
			opacity: 0;
			z-index: -1;
			border-radius: inherit;
			transition: ease 0.25s;
		}
		@media (hover: hover) and (pointer: fine) {
			&:hover {
				&::before {
					opacity: 1;
				}
			}
		}
		@include theme-dark {
			&::before {
				background: rgba(250, 157, 241, 0.08);
			}
			background: linear-gradient(to bottom, rgba(255, 255, 255, 0.04), rgba(250, 157, 241, 0.08));
		}
	}

	// &:active {
	// 	background: rgba(255, 255, 255, 0.4)!important;
	// }

	&__text {
		margin-top: auto;
	}

	.button-blurred__icon,
	.button-blurred__text {
		transition: opacity 0.4s ease;
	}

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		opacity: 0;
		background: linear-gradient(
			90deg,
			transparent 0%,
			rgba(var(--color-active-rgb), 0.08) 50%,
			transparent 100%
		);
		transition: opacity 0.4s ease;
		pointer-events: none;
	}

	&_loading {
		pointer-events: none;

		.button-blurred__icon,
		.button-blurred__text {
			opacity: 0;
		}

		&::after {
			opacity: 1;
			animation: skeleton-shimmer 1.5s ease-in-out infinite;
		}
	}

	&__icon {
		width: var(--button-icon-size, 3.2rem);
		height: var(--button-icon-size, 3.2rem);
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 50%;
		background: var(--color-white);

		svg {
			width: var(--button-svg-size, 1.6rem);
		}

		@include theme-dark {
			background-color: var(--color-panel);
			box-shadow: inset 1px 1px 1px 0px var(--color-panel);
		}
	}

}

@keyframes skeleton-shimmer {
	0% { transform: translateX(-100%); }
	100% { transform: translateX(100%); }
}
</style>
